import {StreamTracker} from '@aboutyou/grpc-clients/tadarida/aysa_api/models//tracker.types';
import type {ProductTileV2} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_tile_v2.types';
import type {FashionUpdate as FashionUpdateService} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/category_page/v1/stream/stream.types';
import {
  HorizontalSlider,
  HorizontalSliderTitle,
  SliderWrapper,
  Slot,
  mediaDesktopAndUp,
  mediaLargeScreens,
  tabletAndUpLinaria,
} from '@aboutyou/ui-components';
import {styled as linariaStyled} from '@linaria/react';
import React, {FC} from 'react';
import sc from 'styled-components';
import {NonProductTileSpacingAdjustment} from '../ProductStream/helpers';
import {ProductTile} from '../ProductTile/ProductTile';
import {FashionUpdateTitle} from './FashionUpdateTitle';
import rectangularBackground from './assets/rectangular-background.svg';
import squareBackground from './assets/square-background.svg';
import {useFashionUpdaterTracking} from './useFashionUpdaterTracking';

const SLIDE_COUNT = {
  slideCount: 2,
  slideCountMedium: 3,
  slideCountLarge: 4,
};

interface FashionUpdateProps
  extends Omit<FashionUpdateService, 'tracker' | 'deprecatedProductTiles'> {
  gridColumns: number | undefined;
  streamPosition: number;
  trackingData: {
    recoType: FashionUpdateService['tracker']['name'];
    sortingChannel: StreamTracker['sortingChannel'];
  };
  hideFashionUpdateSideButtons?: boolean;
}

export const DeprecatedFashionUpdate: FC<FashionUpdateProps> = ({
  gridColumns,
  streamPosition,
  headline,
  productTiles,
  trackingData,
  hideFashionUpdateSideButtons,
}) => {
  const {fashionUpdateTrackingRef, trackOnClick} = useFashionUpdaterTracking();

  const handleOnClick = () => {
    trackOnClick();
  };

  const dataTestid = 'fashionUpdate';

  return (
    <StyledSlot
      columns={gridColumns}
      position={streamPosition}
      dataTestid={dataTestid}
      Comp="li"
    >
      <div
        data-theme="dark"
        ref={fashionUpdateTrackingRef}
        onClick={handleOnClick}
      >
        <StyledHorizontalSlider
          data-testid={`${dataTestid}-slider`}
          slideCount={SLIDE_COUNT.slideCount}
          slideCountMedium={SLIDE_COUNT.slideCountMedium}
          slideCountLarge={SLIDE_COUNT.slideCountLarge}
          title={<FashionUpdateTitle headline={headline} />}
          uniqueKey="fashionUpdate_slider"
          $numberOfTiles={productTiles.length}
          hideSideButtons={hideFashionUpdateSideButtons}
        >
          {productTiles.map((tile, index) => (
            <ProductTile
              key={`fashion-update-product-tile-${
                (tile as unknown as ProductTileV2).productId
              }`}
              tile={tile as unknown as ProductTileV2}
              streamPosition={index}
              trackingData={{
                recoType: trackingData.recoType,
                sortingChannel: trackingData.sortingChannel,
              }}
            />
          ))}
        </StyledHorizontalSlider>
      </div>
    </StyledSlot>
  );
};

const StyledHorizontalSlider = linariaStyled(HorizontalSlider)<{
  $numberOfTiles: number;
}>`
  background-image: url(${squareBackground});
  background-repeat: no-repeat;
  background-size: cover;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-bottom: 2rem;

  ${SliderWrapper} {
    /**
     * The content create an unnecessary space of 60px, and the last child has an unnecessary space of 5px
     * together they create enough space to make the slider with few elements look not centered.
     * This styles remove these spaces
     */
    & > *:last-child {
      margin-right: 0;
    }

    &::after {
      content: none;
    }
  }

  ${tabletAndUpLinaria('border-radius: 5px;')};

  @media ${mediaDesktopAndUp} {
    background-image: url(${rectangularBackground});
  }

  /**
   * Hack to force the slider to center the elements in screen sizes where
   * 3 and 4 product tiles fit perfectly on the screen, as 4 product tiles
   * can fit in a large screen, and 3 in a regular desktop screen
   */

  @media ${mediaDesktopAndUp} {
    ${SliderWrapper} {
      justify-content: ${props => (props.$numberOfTiles === 3 ? 'center' : '')};
      overflow-x: ${props => (props.$numberOfTiles === 3 ? 'hidden' : 'auto')};
    }
  }

  @media ${mediaLargeScreens} {
    ${SliderWrapper} {
      justify-content: ${props => (props.$numberOfTiles === 4 ? 'center' : '')};
      overflow-x: ${props => (props.$numberOfTiles === 4 ? 'hidden' : 'auto')};
    }
  }

  ${HorizontalSliderTitle} {
    flex: 1;
    justify-content: center;
  }
`;

const StyledSlot = sc(Slot)`
  ${NonProductTileSpacingAdjustment}
`;
